import React, { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  updateDoc,
  arrayUnion,
  serverTimestamp,
  getDoc,
  deleteField
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { 
  Building2, 
  Search, 
  Send, 
  CheckCircle, 
  Clock, 
  ArrowRight,
  AlertCircle,
  UserPlus,
  Building
} from 'lucide-react';
import './StaffJoin.css';

function StaffJoin({ user, onSuccess }) {
  // 🎯 상태 관리
  const [step, setStep] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingRequest, setPendingRequest] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  // 🔍 대기 중인 신청 확인
  useEffect(() => {
    if (user?.uid) {
      checkPendingRequest();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // 대기 중인 신청 확인
  const checkPendingRequest = async () => {
    if (!user?.uid) return;

    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.data();

      if (userData?.pendingCompanyId) {
        const companyDoc = await getDoc(doc(db, 'users', userData.pendingCompanyId));
        if (companyDoc.exists()) {
          setPendingRequest({
            companyName: userData.pendingCompanyName,
            companyType: userData.pendingCompanyType,
            companyId: userData.pendingCompanyId
          });
        }
      }
    } catch (error) {
      console.error('대기 중인 신청 확인 실패:', error);
    }
  };

  // 🔍 업체 검색
  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      alert('검색어를 입력하세요.');
      return;
    }

    setSearchLoading(true);
    try {
      // 치과와 기공소만 검색
      const q = query(
        collection(db, 'users'),
        where('businessType', 'in', ['dental', 'clinic', 'lab'])
      );

      const snapshot = await getDocs(q);
      const results = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(company => 
          company.businessName?.toLowerCase().includes(searchTerm.toLowerCase())
        );

      setCompanies(results);

      if (results.length === 0) {
        alert('검색 결과가 없습니다.');
      }
    } catch (error) {
      console.error('검색 실패:', error);
      alert('검색에 실패했습니다: ' + error.message);
    } finally {
      setSearchLoading(false);
    }
  };

  // ✅ 업체 선택
  const selectCompany = (company) => {
    setSelectedCompany(company);
    setStep(2);
  };

  // 📝 신청서 제출
  const handleSubmit = async () => {
    if (!message.trim()) {
      alert('신청 메시지를 입력하세요.');
      return;
    }

    if (!selectedCompany) return;

    setLoading(true);
    try {
      // 사용자 문서에 대기 정보 저장
      await updateDoc(doc(db, 'users', user.uid), {
        pendingCompanyId: selectedCompany.id,
        pendingCompanyName: selectedCompany.businessName,
        pendingCompanyType: selectedCompany.businessType,
        staffJoinRequestedAt: serverTimestamp()
      });

      // 업체 문서에 신청 추가
      await updateDoc(doc(db, 'users', selectedCompany.id), {
        pendingStaff: arrayUnion({
          userId: user.uid,
          email: user.email,
          name: user.name || user.email,
          message: message,
          requestedAt: new Date().toISOString(),
          status: 'pending'
        })
      });

      alert('✅ 직원 신청이 완료되었습니다. 업체 승인을 기다려주세요.');
      
      setPendingRequest({
        companyName: selectedCompany.businessName,
        companyType: selectedCompany.businessType,
        companyId: selectedCompany.id
      });

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('신청 실패:', error);
      alert('신청에 실패했습니다: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // ❌ 신청 취소
  const cancelRequest = async () => {
    if (!window.confirm('신청을 취소하시겠습니까?')) return;

    setLoading(true);
    try {
      // 사용자 문서에서 대기 정보 삭제
      await updateDoc(doc(db, 'users', user.uid), {
        pendingCompanyId: deleteField(),
        pendingCompanyName: deleteField(),
        pendingCompanyType: deleteField(),
        staffJoinRequestedAt: deleteField()
      });

      // 업체 문서에서 신청 제거
      if (pendingRequest?.companyId) {
        const companyDoc = await getDoc(doc(db, 'users', pendingRequest.companyId));
        if (companyDoc.exists()) {
          const companyData = companyDoc.data();
          const updatedPendingStaff = (companyData.pendingStaff || []).filter(
            staff => staff.userId !== user.uid
          );
          
          await updateDoc(doc(db, 'users', pendingRequest.companyId), {
            pendingStaff: updatedPendingStaff
          });
        }
      }

      setPendingRequest(null);
      setStep(1);
      setSearchTerm('');
      setCompanies([]);
      setSelectedCompany(null);
      setMessage('');
      alert('신청이 취소되었습니다.');
    } catch (error) {
      console.error('취소 실패:', error);
      alert('취소에 실패했습니다: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // 🎨 업체 타입 레이블
  const getBusinessTypeLabel = (type) => {
    switch(type) {
      case 'dental':
      case 'clinic':
        return '치과';
      case 'lab':
        return '기공소';
      default:
        return type;
    }
  };

  // 🕐 대기 중인 신청이 있는 경우
  if (pendingRequest) {
    return (
      <div className="staff-join-container">
        <div className="staff-join-header">
          <Clock size={48} className="header-icon pending" />
          <h1>직원 신청 대기중</h1>
          <p>업체의 승인을 기다리고 있습니다</p>
        </div>

        <div className="pending-status-card">
          <div className="status-badge pending">
            <Clock size={16} />
            <span>승인 대기중</span>
          </div>

          <div className="pending-company-info">
            <Building size={32} className="company-icon" />
            <div className="company-details">
              <h3>{pendingRequest.companyName}</h3>
              <p className="company-type">
                {getBusinessTypeLabel(pendingRequest.companyType)}
              </p>
            </div>
          </div>

          <div className="pending-notice">
            <AlertCircle size={18} />
            <div>
              <p><strong>업체 관리자가 신청을 검토 중입니다</strong></p>
              <p>승인이 완료되면 자동으로 소속 업체 직원으로 전환됩니다.</p>
            </div>
          </div>

          <button
            onClick={cancelRequest}
            disabled={loading}
            className="btn-cancel"
          >
            {loading ? '처리 중...' : '신청 취소'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="staff-join-container">
      {/* 헤더 */}
      <div className="staff-join-header">
        <UserPlus size={48} className="header-icon" />
        <h1>업체 직원으로 가입</h1>
        <p>소속 업체를 검색하고 직원 가입 신청을 하세요</p>
      </div>

      {/* Step 인디케이터 */}
      <div className="step-indicator">
        <div className={`step ${step >= 1 ? 'active' : ''}`}>
          <div className="step-number">1</div>
          <div className="step-label">업체 검색</div>
        </div>
        <div className="step-line"></div>
        <div className={`step ${step >= 2 ? 'active' : ''}`}>
          <div className="step-number">2</div>
          <div className="step-label">신청서 작성</div>
        </div>
      </div>

      {/* Step 1: 업체 검색 */}
      {step === 1 && (
        <div className="step-content">
          <div className="search-section">
            <label className="search-label">업체명 검색</label>
            <div className="search-input-group">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="업체명을 입력하세요"
                className="search-input"
                disabled={searchLoading}
              />
              <button
                onClick={handleSearch}
                disabled={searchLoading}
                className="btn-search"
              >
                <Search size={18} />
                {searchLoading ? '검색중...' : '검색'}
              </button>
            </div>
          </div>

          {companies.length > 0 && (
            <div className="search-results">
              <h3 className="results-title">
                검색 결과 <span className="results-count">{companies.length}개</span>
              </h3>
              <div className="company-list">
                {companies.map(company => (
                  <div
                    key={company.id}
                    className="company-card"
                    onClick={() => selectCompany(company)}
                  >
                    <div className="company-card-icon">
                      <Building2 size={24} />
                    </div>
                    <div className="company-card-content">
                      <h4>{company.businessName}</h4>
                      <p className="company-type">
                        {getBusinessTypeLabel(company.businessType)}
                      </p>
                      {company.address && (
                        <p className="company-address">{company.address}</p>
                      )}
                    </div>
                    <div className="company-card-arrow">
                      <ArrowRight size={20} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step 2: 신청서 작성 */}
      {step === 2 && selectedCompany && (
        <div className="step-content">
          <div className="selected-company-card">
            <div className="selected-badge">
              <CheckCircle size={16} />
              선택한 업체
            </div>
            <div className="selected-company-info">
              <Building2 size={32} className="company-icon" />
              <div>
                <h3>{selectedCompany.businessName}</h3>
                <p>{getBusinessTypeLabel(selectedCompany.businessType)}</p>
              </div>
            </div>
          </div>

          <div className="form-section">
            <label className="form-label">
              신청 메시지 <span className="required">*</span>
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="업체에 전달할 메시지를 입력하세요&#10;&#10;예시:&#10;안녕하세요, [이름]입니다.&#10;귀사에서 근무하고 싶어 신청합니다.&#10;잘 부탁드립니다."
              rows={8}
              className="form-textarea"
              maxLength={500}
            />
            <div className="char-count">{message.length} / 500</div>
          </div>

          <div className="action-buttons">
            <button
              onClick={() => {
                setStep(1);
                setSelectedCompany(null);
                setMessage('');
              }}
              className="btn-back"
              disabled={loading}
            >
              이전
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || !message.trim()}
              className="btn-submit"
            >
              <Send size={18} />
              {loading ? '신청 중...' : '신청하기'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default StaffJoin;